import { Payload, useFetchMutation, usePost, useFetch, prepareUrl, APIMethod, useUpdate } from 'api/reactQuery/queryService';
import { objectify, Inline, fetchGet, fetchPost, AsObjectified, objectifyAddress, IObjectified, AsInlineArray } from '../utils';
import { APIRoutes } from './constants';
import { DEFAULT_PAGE_SIZE } from 'components/common/DataGridDevEx/DataGrid.constants';
import { IBatchInfo } from 'components/common/DataGridDevEx/DataGrid.properties';
import { asObjectifiedInlineArray, toObjectified } from 'api/baseApi';
import { IDeletePurchaseOrderLineCriteriaFacade, IDeleteRecordResponse, ILookupResponse, IOutstandingPurchaseOrderLineSeachResponse, IPOCommitLineProductSummaryFacade, IPOCommitLineProductSummaryResponse, IProcessPurchaseOrderFacade, IProcessPurchaseOrderResponse, IPurchaseOrderDeliveryDetailsFacade, IPurchaseOrderDetailsFacade, IPurchaseOrderGridLineFacade, IPurchaseOrderGridLineResponse, IPurchaseOrderLineFacade, IPurchaseOrderLineSummaryResponse, IPurchaseOrderOustandingDocumentSearchResponse, ISearchRequest, IUpdatePurchaseOrderCommitmentDetailResponse, IUpdatePurchaseOrderDetailsCriteriaFacade, IUpdatePurchaseOrderGridLineCriteriaFacade, IUpdatePurchaseOrderLineResponse, IValidatedResponse } from 'api/swaggerTypes';
import { useQueryClient } from '@tanstack/react-query';

const STALETIME = 120000;
export type IObjectifiedPickSlipSummaryResponse = AsObjectified<IPurchaseOrderLineSummaryResponse, 'PurchaseOrderLineSummary'>;

export interface IPurchaseOrdersDetailsResponse {
    values: {
        SupplierPOAddress: {
            addressee: string;
            addressLine1: string;
            addressLine2: string;
            city: string;
            state: string;
            postalCode: string;
            country: string;
        };
        PurchaseOrderDetails?: IPurchaseOrderDetailsFacade;
    };
    schema: IPurchaseOrderDetailsFacade;
}

export const search = (query) => {
    const { SearchText, BatchPage, BatchSize = DEFAULT_PAGE_SIZE, Filters } = query;
    const respType = 'Inline';

    let filters = {};
    if (Filters) {
        filters = {
            ...Filters,
            Status: Filters.Status === '0' ? '' : Filters.Status,
            Finalised: Filters.Finalised === '0' ? '' : Filters.Finalised,
            Supplier: Filters?.Supplier ? parseInt(Filters?.Supplier) : undefined
        };
    }

    return fetchPost('/PurchaseOrders/Search', { SearchText, BatchPage, BatchSize, ...(filters ?? {}) }, null, respType)
        .then((response) => ({
            records: response.PurchaseOrders.map((val) => ({ inlineObject: objectify(val), schema: val })),
            currPage: response.BatchInformation && response.BatchInformation.BatchPage,
            nextPage: response.BatchInformation && !response.BatchInformation.EndOfData,
            prevPage: response.BatchInformation && response.BatchInformation.BatchPage > 1
        }));
};

export const searchById = (PurchaseOrder) => {
    return fetchGet(`PurchaseOrderEnquiry/PurchaseOrder/${PurchaseOrder}/Summary`)
        .then((result) => ({
            records: result ? [{ inlineObject: objectify(result.PurchaseOrderSummary), schema: result.PurchaseOrderSummary }] : [],
            currPage: 1,
            nextPage: false,
            prevPage: false
        }));
};

const purchaseOrdersLinesResponseParser = (response): any => {
    const { PurchaseOrdersLines } = response;

    return {
        ...response,
        PurchaseOrdersLines: PurchaseOrdersLines.map((o) => objectify(o as Inline<any>))
    };
};

export type IRetrievePurchaseOrdersLinesPayload = Payload<{ PurchaseOrder: string }, IBatchInfo>;

export const useRetrievePurchaseOrdersLines = () =>
    useFetchMutation<IRetrievePurchaseOrdersLinesPayload, any>
        (APIRoutes.purchaseOrders.lines.retrieve, null, { inline: true, responseParser: purchaseOrdersLinesResponseParser });

const loadPurchaseOrderResponseParser = (response): any => {

    return response;
};

export type ILoadPurchaseOrderPayload = Payload<{ PurchaseOrder: string }, IBatchInfo>;
export const useLoadPurchaseOrder = () =>
    usePost<ILoadPurchaseOrderPayload, any>
        (APIRoutes.purchaseOrders.load.purchaseOrder, null, { inline: true, responseParser: loadPurchaseOrderResponseParser });

const createPurchaseOrderResponseParser = (response): any => {

    return response;
};
export const useCreatePurchaseOrder = () =>
    usePost<any, any>
        (APIRoutes.purchaseOrders.create.purchaseOrder, null, { inline: true, responseParser: createPurchaseOrderResponseParser });

const purchaseOrdersSummaryResponseParser = (response): any => {
    const { PurchaseOrderSummary } = response;

    return {
        ...response,
        PurchaseOrderSummary: toObjectified(PurchaseOrderSummary as Inline<any>)
    };
};
export const useRetrievePurchaseOrdersSummary = (PurchaseOrder: string) =>
    useFetch<any>(
        {
            url: APIRoutes.purchaseOrders.summary.retrieve,
            urlParams: { PurchaseOrder },
            queryConfig: { enabled: !!PurchaseOrder, staleTime: STALETIME },
            options: { inline: true, responseParser: purchaseOrdersSummaryResponseParser }
        }
    );

const purchaseOrdersLineDetailsResponseParser = (response): any => {
    const { PurchaseOrderLineDetails } = response;

    return {
        ...response,
        PurchaseOrderLineDetails: toObjectified(PurchaseOrderLineDetails as Inline<any>)
    };
};

export const useRetrievePurchaseOrdersLineDetails = (PurchaseOrder: string, LineNumber: number) =>
    useFetch<any>(
        {
            url: APIRoutes.purchaseOrders.lines.details,
            urlParams: { PurchaseOrder, LineNumber },
            queryConfig: { enabled: (PurchaseOrder && PurchaseOrder !== '') && LineNumber > 0 },
            options: { inline: true, responseParser: purchaseOrdersLineDetailsResponseParser }
        }
    );

export const useSetPurchaseOrdersLineDetailsQueryData = () => {
    const queryClient = useQueryClient();

    return (PurchaseOrder, LineNumber, dataToUpdate: any) => {
        const finalUrl = prepareUrl(APIRoutes.purchaseOrders.lines.details, { PurchaseOrder, LineNumber }, null, true);
        queryClient.setQueryData([APIMethod.GET, finalUrl], dataToUpdate);
    };
};

const lineDetailResponseParser = (response) => {

    return response;
};

export const useUpdatePurchaseOrdersLine = () =>
    usePost<any, any>(
        APIRoutes.purchaseOrders.lines.updateDetails,
        null,
        { inline: true, responseParser: lineDetailResponseParser }
    );

const purchaseOrdersLineDetailResponseParser = (response) => ({
    ...response,
    PurchaseOrderLineDetails: response?.PurchaseOrderLineDetails ? toObjectified(response?.PurchaseOrderLineDetails as Inline<IPurchaseOrderLineFacade>) : null
});

export type ICalculateLinePayload = Payload<IPurchaseOrderLineFacade & { PurchaseOrder?: number; LineNumber?: number }, { ChangedField?: string }>;
export const useCalculateLine = () =>
    usePost<ICalculateLinePayload, any>(
        APIRoutes.purchaseOrders.lines.calculateDetails,
        null,
        { inline: true, responseParser: purchaseOrdersLineDetailResponseParser }
    );

const purchaseOrderDetailsResponseParser = (response) => {
    const PurchaseOrderDetails = response.PurchaseOrderDetails && toObjectified(response.PurchaseOrderDetails as Inline<IPurchaseOrderDetailsFacade>);

    return {
        ...response,
        PurchaseOrderDetails: {
            inlineObject: {
                ...PurchaseOrderDetails?.inlineObject,
                address: objectifyAddress(PurchaseOrderDetails?.inlineObject, 'Supplier')
            },
            schema: response.PurchaseOrderDetails
        }
    };
};

export const useRetrievePurchaseOrderDetails = (PurchaseOrder: string) =>

    useFetch<any>(
        {
            url: APIRoutes.purchaseOrders.details.retrieve,
            urlParams: { PurchaseOrder },
            queryConfig: { enabled: !(PurchaseOrder === '') },
            options: { inline: true, responseParser: purchaseOrderDetailsResponseParser }
        }
    );

export const useCalculatePurchaseOrderDetails = () => {

    return useUpdate<Payload<{ PurchaseOrder: string } & IUpdatePurchaseOrderDetailsCriteriaFacade>, any>
        (APIRoutes.purchaseOrders.details.calculate, null, { inline: true, responseParser: purchaseOrderDetailsResponseParser });
};

export const useUpdatePurchaseOrderDetails = () => {

    return usePost<Payload<{ PurchaseOrder: string } & IUpdatePurchaseOrderDetailsCriteriaFacade>, any>
        (APIRoutes.purchaseOrders.details.update, null, { inline: true, responseParser: purchaseOrderDetailsResponseParser });
};

export const useSetPurchaseOrderDetailsQueryData = () => {
    const queryClient = useQueryClient();

    return (PurchaseOrder: string, dataToUpdate: any) => {
        const finalUrl = prepareUrl(APIRoutes.purchaseOrders.details.retrieve, { PurchaseOrder }, null, true);
        queryClient.setQueryData([APIMethod.GET, finalUrl], dataToUpdate);
    };
};

const purchaseOrderDeliveryResponseParser = (response) => {
    const PurchaseOrderDeliveryDetails = response.PurchaseOrderDeliveryDetails && toObjectified(response.PurchaseOrderDeliveryDetails as Inline<IPurchaseOrderDeliveryDetailsFacade>);

    return {
        ...response,
        PurchaseOrderDeliveryDetails: {
            inlineObject: {
                ...PurchaseOrderDeliveryDetails?.inlineObject,
                DeliveryAddress: objectifyAddress(PurchaseOrderDeliveryDetails?.inlineObject, 'Delivery')
            },
            schema: response.PurchaseOrderDeliveryDetails
        }
    };
};

export const useFetchPODelivery = (PurchaseOrder: string) => {
    return useFetch<any>(
        {
            url: APIRoutes.purchaseOrders.delivery.retrieve,
            urlParams: { PurchaseOrder },
            queryConfig: { enabled: (PurchaseOrder && PurchaseOrder !== '') },
            options: { inline: true, responseParser: purchaseOrderDeliveryResponseParser }
        }
    );
};

export const useCalculatePODeliveryDetails = () => {

    return useUpdate<Payload<{ PurchaseOrder?: number | string; urlQueryParams?: any }>, any>
        (APIRoutes.purchaseOrders.delivery.calculate, null, { inline: true, responseParser: purchaseOrderDeliveryResponseParser });
};

export const useSetPurchaseOrdersDeliveryQueryData = () => {
    const queryClient = useQueryClient();

    return (PurchaseOrder, dataToUpdate: any) => {
        const finalUrl = prepareUrl(APIRoutes.purchaseOrders.delivery.retrieve, { PurchaseOrder }, null, true);
        queryClient.setQueryData([APIMethod.GET, finalUrl], dataToUpdate);
    };
};

export const useUpdatePurchaseOrdersDelivery = () =>
    usePost<any, any>(
        APIRoutes.purchaseOrders.delivery.update,
        null,
        { inline: true, responseParser: purchaseOrderDeliveryResponseParser }
    );
export const useDeletePurchaseOrderLine = () => {

    return usePost<Payload<{ PurchaseOrder: string; LineNumber: number } & IDeletePurchaseOrderLineCriteriaFacade>, IDeleteRecordResponse>
        (APIRoutes.purchaseOrders.lines.delete, null, { inline: true });
};

export type IObjectifiedPurchaseOrderGridLineResponse = Omit<IPurchaseOrderGridLineResponse, 'PurchaseOrderGridLine'> & {
    PurchaseOrderGridLine: IObjectified<IPurchaseOrderGridLineFacade>;
};

const purchaseOrderGridLineResponseParser = (response) => {

    return {
        ...response,
        PurchaseOrderGridLine: response.PurchaseOrderGridLine && toObjectified(response.PurchaseOrderGridLine as Inline<IPurchaseOrderGridLineFacade>)
    };
};

export const useCalculatePurchaseOrderLine = () => {

    return usePost<Payload<{ PurchaseOrder: string; LineNumber: number; urlQueryParams: { ChangedField: string } } & IPurchaseOrderGridLineFacade>, IObjectifiedPurchaseOrderGridLineResponse>
        (APIRoutes.purchaseOrders.lines.calculate, null, { inline: true, responseParser: purchaseOrderGridLineResponseParser });
};

export const useUpdatePurchaseOrderLine = () => {

    return usePost<Payload<{ PurchaseOrder: string; LineNumber: number } & IUpdatePurchaseOrderGridLineCriteriaFacade>, IUpdatePurchaseOrderLineResponse>
        (APIRoutes.purchaseOrders.lines.update, null, { inline: true });
};

export const useRetrievePurchaseOrdersProcessActions = () => {

    return usePost<Payload<{ SearchText: string; PurchaseOrder: string }>, ILookupResponse>
        (APIRoutes.purchaseOrders.process.actions.retrieve, null, { inline: true });
};

const processPurchaseOrdersResponseParser = (response) => {
    const ProcessPurchaseOrder = response.ProcessPurchaseOrder && toObjectified(response.ProcessPurchaseOrder as Inline<IProcessPurchaseOrderFacade>);

    return {
        ...response,
        ProcessPurchaseOrder: {
            inlineObject: ProcessPurchaseOrder?.inlineObject || {},
            schema: response.ProcessPurchaseOrder,
        }
    };
};

export const useProcessPurchaseOrders = () => {

    return usePost<Payload<{ PurchaseOrder: string }>, IProcessPurchaseOrderResponse>
        (APIRoutes.purchaseOrders.process.purchaseOrders, null, { inline: true, responseParser: processPurchaseOrdersResponseParser });
};

export const addLine = () =>
    usePost<Payload<{
        PurchaseOrder: string;
        PurchaseOrderLine: {
            ProductId: number;
            OrderedQuantity: number;
            Price: number;
        };
    } & IUpdatePurchaseOrderGridLineCriteriaFacade>, any>
        (APIRoutes.purchaseOrders.fastEntry.insert, null, { inline: true });

export const useCalculateFastEntryDetails = () => {

    return usePost<Payload<any & { PurchaseOrder: number }, { ChangedField: string }>, any>
        (APIRoutes.purchaseOrders.fastEntry.calculateFastLine, null);
};

export const useUpdatePurchaseOrdersStatus = () => {

    return usePost<Payload<{ PurchaseOrder: string; SetStatus: string; urlQueryParams: { UserId?: string } }>, IValidatedResponse>
        (APIRoutes.purchaseOrders.process.setStatus, null, { inline: true });
};

export type IRetrievePurchaseOrdersCommitLinesPayload = Payload<{ PurchaseOrder: string }, IBatchInfo & { CommitQuantityFilter?: string }>;

export const useRetrievePurchaseOrdersCommitLines = () =>
(
    usePost<IRetrievePurchaseOrdersCommitLinesPayload, AsInlineArray<IOutstandingPurchaseOrderLineSeachResponse, 'OutstandingPurchaseOrderLines'>>
        (
            APIRoutes.purchaseOrders.commit.retrieve, null,
            {
                inline: true,
                responseParser: (response: AsInlineArray<IOutstandingPurchaseOrderLineSeachResponse, 'OutstandingPurchaseOrderLines'>) =>
                    asObjectifiedInlineArray(response, 'OutstandingPurchaseOrderLines')
            }
        )
);

export type IRetrievePurchaseOrdersLineCommitmentsPayload = Payload<{ PurchaseOrder: string; LineNumber: number }, IBatchInfo & { FilterByUncommitted?: boolean }>;

export const useRetrievePurchaseOrdersLineCommitments = () =>
(
    useFetchMutation<IRetrievePurchaseOrdersLineCommitmentsPayload, AsInlineArray<IPurchaseOrderOustandingDocumentSearchResponse, 'PurchaseOrderOustandingDocuments'>>
        (
            APIRoutes.purchaseOrders.lineCommitments.retrieve,
            null,
            {
                inline: true,
                responseParser: (response: AsInlineArray<IPurchaseOrderOustandingDocumentSearchResponse, 'PurchaseOrderOustandingDocuments'>) =>
                    asObjectifiedInlineArray(response, 'PurchaseOrderOustandingDocuments')
            }
        )
);

export type IUpdatePurchaseOrdersLineCommitmentsPayload = Payload<{ PurchaseOrder: string; LineNumber: number }>;

export const useUpdateLineCommitments = () =>
(
    usePost<IUpdatePurchaseOrdersLineCommitmentsPayload, IUpdatePurchaseOrderCommitmentDetailResponse>
        (
            APIRoutes.purchaseOrders.lineCommitments.update,
            null,
            { inline: true }
        )
);

export const useRetrievePurchaseOrdersLineCommitmentsSummary = (PurchaseOrder: string, LineNumber: number) =>
(
    useFetch<IPOCommitLineProductSummaryResponse>(
        {
            url: APIRoutes.purchaseOrders.lineCommitments.summary,
            urlParams: { PurchaseOrder, LineNumber },
            options: {
                inline: true,
                responseParser: (response: IPOCommitLineProductSummaryResponse) => (toObjectified(response.PurchaseOrderCommitLineSummary as Inline<IPOCommitLineProductSummaryFacade>)?.inlineObject)
            }
        }
    )
);

const purchaseOrderLookupSearchResponseParser = (response): Promise<any> => {
    const { Data } = response;

    return {
        ...response,
        PurchaseOrders: Data.map((o) => objectify(o as Inline<any>))
    };
};

export const useSearchPurchaseOrdersLookup = () =>
    usePost<Payload<ISearchRequest>, any>(
        APIRoutes.purchaseOrders.searchLookup,
        null,
        { inline: true, responseParser: purchaseOrderLookupSearchResponseParser }
    );
